/**
 * Media Upload Utility
 * Handles file uploads to API media endpoint with JWT authentication
 */

import { mediaRequest } from './queryClient';

/**
 * Uploads a file to the API media endpoint
 * @param file - File to upload
 * @param mediaKey - The media key/path where the file should be uploaded
 * @returns Promise<string> - The media key/path for the uploaded file
 */
export async function uploadToMediaStorage(
  file: File, 
  mediaKey: string
): Promise<string> {
  // Upload the file directly as the request body with the correct Content-Type
  const response = await mediaRequest(
    'PUT',
    `/${mediaKey}`,
    file
  );

  if (!response.ok) {
    throw new Error(`Media upload failed: ${response.status} ${response.statusText}`);
  }

  // Return the media key (the API should handle storage and return the key)
  return mediaKey;
}

/**
 * Generate a media key for import folder storage
 * @param fileExtension - The file extension (e.g., 'jpg', 'png')
 * @param originalName - Optional original filename to preserve
 * @returns string - The generated media key for import folder
 */
export function generateImportMediaKey(fileExtension: string, originalName?: string): string {
  let filename: string;
  
  if (originalName) {
    // Use original filename but sanitize it and add timestamp to avoid conflicts
    const baseName = originalName.replace(/\.[^/.]+$/, ""); // Remove extension
    const sanitizedName = baseName.replace(/[^a-zA-Z0-9-_]/g, '_'); // Sanitize
    const timestamp = Date.now();
    filename = `${timestamp}-${sanitizedName}.${fileExtension}`;
  } else {
    // Generate unique filename with timestamp (fallback behavior)
    const timestamp = Date.now();
    filename = `${timestamp}-${Math.random().toString(36).substring(2)}.${fileExtension}`;
  }
  
  // Use the import folder path as the key
  return `import/${encodeURIComponent(filename)}`;
}